import React, { useEffect, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'

import { useGTMDispatch } from '@elgorditosalsero/react-gtm-hook'
import { notification } from 'antd'
import MessageModal from 'components/Modals/MessageModal'
import { RiseUpErrors } from 'constants/errors'
import useErrorMessage from 'hooks/useErrorMessage'
import { useGTMData } from 'hooks/useGTMData'
import { useUTMTracking } from 'hooks/useUTMTracking'
import { max } from 'lodash'
import queryString from 'query-string'
import swal from 'sweetalert'
import { getModifiedUrlWithStep } from 'utils'
import { consoleLog } from 'utils/simpleLogger'
import { default as AFLogo } from '../../../assets/afbc.png'
import Side4 from '../../../assets/new_side_17.jpg'
import Button from '../../../components/Button'
import ErrorMessage from '../../../components/ErrorMessage'
import FormLabel from '../../../components/FormLabel'
import InputField from '../../../components/InputField'
import LoaderModal from '../../../components/Modals/LoaderModal'
import NonAccreditedInvestorModal from '../../../components/Modals/NonAccreditedInvestorModal'
import Stepper from '../../../components/Stepper'
import { useRiseUpAPIV1 } from '../../../hooks/useRiseUpAPIV1'
import { IFormProps, InvestingPersonEnum, RetirementAccountEnum } from '../../../interfaces'

const FinancialStanding = (props: IFormProps) => {
  const minInvestment = Number(process.env.REACT_APP_MIN_INVESTMENT)
  const [isLoading, setIsLoading] = useState(false)
  const [isError, setIsError] = useState(false)
  const [maxInvestmentAmount, setMaxInvestmentAmount] = useState(0)
  const [isMaxInvestmentValid, setIsMaxInvestmentValid] = useState(true)

  const { errorMessage, setErrorMessage } = useErrorMessage()
  const { updateOnboardProgress } = useRiseUpAPIV1()
  const location = useLocation()
  const sendDataToGTM = useGTMDispatch()
  const gtmData = useGTMData(props.getValues)
  const {
    getFormattedUTMParams, // Helper to generate a complete GTM event object
  } = useUTMTracking()
  const navigate = useNavigate()

  const validateAllFields = async () => {
    let validFields = ['unAccreditedInvestor.annualIncome', 'unAccreditedInvestor.netWorth']

    // remove regCFInvestment field if regulation is reg-cf
    if (props.getValues('regulation') === 'reg-cf') {
      // validFields = validFields.filter(e => e !== 'unAccreditedInvestor.regCFInvestment');
      validFields.push('unAccreditedInvestor.regCFInvestment')
    }
    // validate fields
    await props.validateFields(validFields)
  }

  useEffect(() => {
    const queryParams = queryString.parse(location.search)
    const regulation = queryParams.regulation

    props.setValue('regulation', regulation)
  }, [location.search]) // eslint-disable-line

  useEffect(() => {
    props.clearErrors()
  }, [isError]) // eslint-disable-line

  const regCFValidation = () => {
    let maxInvestmentAmount: number = 0
    const investmentAmountInput = props.getValues('investingProfile.amount').toString().replace(/[$,]/g, '')
    const annualIncome = Number(props.getValues('unAccreditedInvestor.annualIncome').toString().replace(/[$,]/g, ''))
    const netWorth = Number(props.getValues('unAccreditedInvestor.netWorth').toString().replace(/[$,]/g, ''))
    const biggerNumberAiNw = max([annualIncome, netWorth]) as number
    /**
     * Cat 1
     * If annual income or the net worth is less than $124000
     * then Investment amount limit should
     * either be $2500 (if 5% of annualIncome or networth is lower than 2500)
     * or 5% of either annualIncome or netWorth (whichever is higher)
     */
    if (annualIncome < 124000 || netWorth < 124000) {
      const maxAiNw = biggerNumberAiNw * 0.05
      maxInvestmentAmount = max([2500, maxAiNw]) as number
    } else if (annualIncome >= 124000 && netWorth >= 124000) {
      /**
       * else if net worth and annual income are equal to or more than 124000
       * then investment amount can be 10% of either annualIncome or networth(whichever is greater)
       * but not more than 124000
       */
      const maxAiNw = biggerNumberAiNw * 0.1
      if (maxAiNw <= 124000) {
        maxInvestmentAmount = maxAiNw as number
      } else {
        maxInvestmentAmount = 124000
      }
    }
    setMaxInvestmentAmount(maxInvestmentAmount)
    props.setValue('temp.investmentAmount', String(maxInvestmentAmount))

    setIsMaxInvestmentValid(maxInvestmentAmount >= minInvestment)

    return investmentAmountInput <= maxInvestmentAmount
  }

  const regAValidation = () => {
    let maxInvestmentAmount: number = 0
    const investmentAmountInput = props.getValues('investingProfile.amount').toString().replace(/[$,]/g, '')

    const annualIncome = Number(props.getValues('unAccreditedInvestor.annualIncome').toString().replace(/[$,]/g, ''))
    const netWorth = Number(props.getValues('unAccreditedInvestor.netWorth').toString().replace(/[$,]/g, ''))
    const biggerNumberAiNw = max([annualIncome, netWorth]) as number
    maxInvestmentAmount = biggerNumberAiNw * 0.1
    setMaxInvestmentAmount(maxInvestmentAmount)
    props.setValue('temp.investmentAmount', String(maxInvestmentAmount))

    setIsMaxInvestmentValid(maxInvestmentAmount >= minInvestment)

    return investmentAmountInput <= maxInvestmentAmount
  }

  const handleSubmit = async () => {
    console.log('submit')
    // validate fields
    await validateAllFields()

    // return nothing if fields has invalid values
    if (!props.isValid) {
      notification.warning({
        duration: 2,
        message: 'Form has error',
        placement: 'topRight',
        description: 'Complete all required fields with correct format.',
        key: 'form-notif',
      })
      return
    }

    setIsLoading(true)
    console.log(isError)
    try {
      if (props.getValues('regulation') === 'reg-cf') {
        if (regCFValidation()) {
          const data = {
            ...props.getValues(),
            lastCompletedStage: '7',
          }
          const updateOnboardProgressRes = await updateOnboardProgress(data)

          if (updateOnboardProgressRes.status === 201 || updateOnboardProgressRes.status === 200) {
            setIsError(false)
            setIsLoading(false)
            props.nextStep('7')
          }
        } else {
          // TODO Add handling for amounts more than the limits
          setIsError(true)
          setIsLoading(false)
        }
      } else if (props.getValues('regulation') === 'reg-a') {
        console.log('reg-a validation')
        console.log(regAValidation())
        if (regAValidation()) {
          const data = {
            ...props.getValues(),
            lastCompletedStage: '7',
          }
          const updateOnboardProgressRes = await updateOnboardProgress(data)
          consoleLog(updateOnboardProgressRes)

          const formattedUTMParams = getFormattedUTMParams()
          const pathname = location.pathname
          const currentParams = queryString.parse(location.search)
          const updatedUrl = getModifiedUrlWithStep(pathname, currentParams, 'acknowledgement')

          if (updateOnboardProgressRes.status === 201 || updateOnboardProgressRes.status === 200) {
            setIsError(false)
            setIsLoading(false)
            sendDataToGTM({
              event: 'funnel_step',
              step_title: 'UNACCREDITED',
              ...gtmData,
              ...formattedUTMParams,
            })
            navigate(updatedUrl)
            props.nextStep('7')
          }
        } else {
          // TODO Add handling for amounts more than the limits
          setIsError(true)
          setIsLoading(false)
        }
      }
    } catch (error: any) {
      console.log(error)
      consoleLog(error)
      setIsLoading(false)
      // catch the duplicate errors
      if (error.response.data === RiseUpErrors.DuplicatePhoneNumber) {
        setErrorMessage(RiseUpErrors.DuplicatePhoneNumber + ' Please update the phone number.')
        return
      }
      if (error.response.data === RiseUpErrors.DuplicateSSN) {
        setErrorMessage(RiseUpErrors.DuplicateSSN + ' Please update the ssn.')
        return
      }
      swal({
        title: 'Error!',
        text: 'Please check if your details are valid and correct. If error persists, contact us.',
        icon: 'error',
      })
    }
  }

  const isSubmitDisabled = () => {
    let unAccreditedInvestor = props.getValues('unAccreditedInvestor')
    const annual = unAccreditedInvestor?.annualIncome?.substr(1) || 0
    const netWorth = unAccreditedInvestor?.netWorth?.substr(1) || 0
    const regCFInvestment = unAccreditedInvestor?.regCFInvestment?.substr(1) || 0

    if (
      parseFloat(annual) <= 0 ||
      annual === '' ||
      parseFloat(netWorth) <= 0 ||
      netWorth === '' ||
      (props.getValues('regulation') === 'reg-cf' && (parseFloat(regCFInvestment) <= 0 || regCFInvestment === ''))
    ) {
      return true
    }
    return false
  }

  // useEffect(() => {
  //   sendDataToGTM({
  //     event: 'funnel_step',
  //     step_title: 'UNACCREDITED',
  //     ...gtmData,
  //   })
  // }, [])

  return (
    <div className="flex h-screen w-full flex-col md:flex-row md:justify-start">
      <div className="mt-10 flex flex-col sm:w-screen md:mt-0 md:min-h-screen md:w-[45%] lg:w-[40%] xl:w-[35%]">
        <div className="relative w-screen px-10 sm:h-auto sm:px-16 md:hidden">
          <img className="w-screen" src={AFLogo} alt="CompanyLogo" />
        </div>
        <div className="relative hidden flex-grow md:flex">
          <img className="flex h-screen w-full" src={Side4} alt={`${Side4}`} />
        </div>
      </div>

      <div className="flex flex-col p-10 sm:w-[100%] md:w-[55%] lg:h-screen lg:w-[60%] lg:overflow-y-scroll xl:w-[65%]">
        {/* <div className="justify-center my-10 hidden lg:flex">
          <div className="w-1/2">
            <img className="w-screen" src={CompanyLogo} alt="CompanyLogo" />
          </div>
        </div> */}

        <div className="md:my-3">
          <Stepper current={props.currentStage} />
        </div>

        <div className="py-4 text-left text-2xl font-semibold text-gray-900 md:py-12 md:pb-10 md:text-4xl">
          Please provide the following:
        </div>

        <div className="md::mt-4 my-4 flex text-base md:text-2xl">
          Investment Commitment:{' '}
          <p className="ml-2 font-bold">
            ${props.getValues('investingProfile.amount').toString().replace(/[$,]/g, '')}
          </p>
        </div>

        <div>
          <div className="my-4 mr-4 w-full lg:my-0">
            <FormLabel
              labelName={
                props.getValues('investingProfile.option') === InvestingPersonEnum.JOINTLY
                  ? 'Combined Annual Income'
                  : 'Annual Income'
              }
            />
            <InputField
              registerField={props.registerField}
              fieldId={'unAccreditedInvestor.annualIncome'}
              validate={{
                numbersOnly: (value: any) => /^[0-9.$,]+$/.test(value),
                minimumAmount: (value: any) => value.replace(/[$,]/g, '') >= 1,
              }}
              maskedInput={'numeric'}
              control={props.control}
              prefix={'$'}
              thousandsGroupStyle={'thousand'}
              thousandSeparator={true}
              decimalScale={2}
              value={props.getValues('unAccreditedInvestor.annualIncome')}
              maskType={'numeric'}
              placeholder={'$ 1.00'}
              onChange={() => props.clearErrors('unAccreditedInvestor.annualIncome')}
            />
            {props.errors.unAccreditedInvestor?.annualIncome?.type === 'numbersOnly' ||
              (props.errors.unAccreditedInvestor?.annualIncome?.type === 'required' && (
                <ErrorMessage message={'Must input a valid amount.'} />
              ))}
            {props.errors.unAccreditedInvestor?.annualIncome?.type === 'minimumAmount' && (
              <ErrorMessage message={'Minimum amount is $1.'} />
            )}
          </div>

          <div className="my-4 mr-4 w-full lg:my-0">
            <FormLabel
              labelName={
                props.getValues('investingProfile.option') === InvestingPersonEnum.JOINTLY
                  ? 'Combined Net Worth'
                  : 'Net Worth'
              }
            />
            <InputField
              registerField={props.registerField}
              fieldId={'unAccreditedInvestor.netWorth'}
              validate={{
                numbersOnly: (value: any) => /^[0-9.$,]+$/.test(value),
                minimumAmount: (value: any) => value.replace(/[$,]/g, '') >= 1,
              }}
              maskedInput={'numeric'}
              control={props.control}
              prefix={'$'}
              thousandsGroupStyle={'thousand'}
              thousandSeparator={true}
              decimalScale={2}
              value={props.getValues('unAccreditedInvestor.netWorth')}
              maskType={'numeric'}
              placeholder={'$ 1.00'}
              onChange={() => props.clearErrors('unAccreditedInvestor.netWorth')}
            />
            {props.errors.unAccreditedInvestor?.netWorth?.type === 'numbersOnly' ||
              (props.errors.unAccreditedInvestor?.netWorth?.type === 'required' && (
                <ErrorMessage message={'Must input a valid amount.'} />
              ))}
            {props.errors.unAccreditedInvestor?.netWorth?.type === 'minimumAmount' && (
              <ErrorMessage message={'Minimum amount is $1.'} />
            )}
          </div>

          {props.getValues('regulation') === 'reg-cf' && (
            <div className="my-4 mr-4 w-full lg:my-0">
              <FormLabel labelName={'How much have you invested over the last 12 months in Regulation CF Offerings'} />
              <InputField
                registerField={props.registerField}
                fieldId={'unAccreditedInvestor.regCFInvestment'}
                validate={{
                  numbersOnly: (value: any) => /^[0-9.$,]+$/.test(value),
                  minimumAmount: (value: any) => value.replace(/[$,]/g, '') >= 0,
                }}
                maskedInput={'numeric'}
                control={props.control}
                prefix={'$'}
                thousandsGroupStyle={'thousand'}
                thousandSeparator={true}
                decimalScale={2}
                value={props.getValues('unAccreditedInvestor.regCFInvestment')}
                maskType={'numeric'}
                placeholder={'$ 0'}
              />
              {props.errors.unAccreditedInvestor?.regCFInvestment?.type === 'numbersOnly' ||
                (props.errors.unAccreditedInvestor?.regCFInvestment?.type === 'required' && (
                  <ErrorMessage message={'Must input a valid amount.'} />
                ))}
              {props.errors.unAccreditedInvestor?.regCFInvestment?.type === 'minimumAmount' && (
                <ErrorMessage message={'Minimum amount is $0.'} />
              )}
            </div>
          )}
        </div>
        {props.getValues('investingProfile.option') === 'RETIREMENT' &&
        props.getValues('investingProfile.retirementAccountType') === RetirementAccountEnum.TRUST_ACCOUNT ? (
          <div className="mt-4 text-base leading-5 text-gray-500">
            The law limits the amount a trust may invest based on its annual income and net worth unless the trust is an
            “accredited investor.” Please provide both numbers so we may determine if the amount the trust wants to
            invest is within these limitations.
          </div>
        ) : null}

        {props.getValues('investingProfile.option') === 'RETIREMENT' &&
        props.getValues('investingProfile.retirementAccountType') === RetirementAccountEnum.RETIREMENT_ACCOUNT ? (
          <div className="mt-4 text-base leading-5 text-gray-500">
            The law limits the amount anyone may invest based on your annual income and your net worth unless you are an
            “accredited investor.” Please provide both numbers so we may determine if the amount you want to invest
            through your retirement account in within these limitations.
          </div>
        ) : null}

        {props.getValues('investingProfile.option') !== 'RETIREMENT' ? (
          <>
            <div className="mt-4 text-base leading-5 text-gray-500">
              {props.getValues('regulation') === 'reg-cf' &&
                props.getValues('investingProfile.option') === 'JOINTLY' &&
                'The law limits the amount joint investors may invest in a Regulation A offering based on your combined annual income and combined net worth unless you are considered to be “accredited investors.” Please provide both numbers so we may determine if the amount you want to jointly invest is within these limitations.'}
              {props.getValues('regulation') === 'reg-cf' &&
                props.getValues('investingProfile.option') !== 'JOINTLY' &&
                'The law limits the amount anyone may invest based on your annual income, your net worth, and the amount you have invested in the past 12 months in Regulation CF offerings, unless you are an "accredited investor." Please provide all three numbers so we may determine if the amount you want invest is within the limitations.'}
              {props.getValues('regulation') === 'reg-a' &&
                props.getValues('investingProfile.option') === 'JOINTLY' &&
                'The law limits the amount a company may invest in a Regulation A offering based on on your combined annual income and combined net worth unless you are considered to be “accredited investors.” Please provide both numbers so we may determine if the amount you want to invest jointly is within these limitations.'}
              {props.getValues('regulation') === 'reg-a' &&
                props.getValues('investingProfile.option') === 'IN_BEHALF_OF_AN_ENTITY' &&
                'The law limits the amount a company may invest in a Regulation A offering based on its annual income and its net worth unless the company is an "accredited investor." Please provide both numbers so we may determine if the amount the company wants to invest is within these limitations.'}
              {props.getValues('regulation') === 'reg-a' &&
                props.getValues('investingProfile.option') === 'FOR_MYSELF' &&
                'The law limits the amount an individual may invest in a Regulation A offering based on your annual income and net worth unless the individual is an "accredited investor." Please provide both numbers so we may determine if the amount you want to invest is within these limitations.'}
            </div>
          </>
        ) : null}

        <div className="w-full justify-center gap-3 py-10 sm:block lg:flex">
          <div className="flex flex-col sm:w-[100%] lg:w-[20%]">
            <Button btnName={'Back'} onClickHandler={() => props.previousStep('6')} />
          </div>
          <div className="flex flex-col sm:w-[100%] lg:w-[80%]">
            <Button
              btnName={'Next: Subscription Agreement'}
              btnType={'button'}
              onClickHandler={() => {
                handleSubmit()
              }}
              disabled={isSubmitDisabled()}
            />
          </div>
        </div>
      </div>
      <LoaderModal isVisible={isLoading} />
      <NonAccreditedInvestorModal
        isVisible={isError}
        onDismiss={() => {
          setIsError(false)
        }}
        getValues={props.getValues}
        maxInvestmentAmount={maxInvestmentAmount}
        registerField={props.registerField}
        control={props.control}
        errors={props.errors}
        setValue={props.setValue}
        nextStep={props.nextStep}
        handleSubmit={updateOnboardProgress}
        isValid={props.isValid as boolean}
        validateFields={props.validateFields}
        clearErrors={props.clearErrors}
        isMaxInvestmentValid={isMaxInvestmentValid}
      />
      <MessageModal
        isVisible={errorMessage !== ''}
        onProceed={() => {
          // automatically set the step to appropriate form duplicate error
          if (errorMessage === RiseUpErrors.DuplicatePhoneNumber + ' Please update the phone number.') {
            setErrorMessage('')
            props.nextStep('3')
          }
          if (errorMessage === RiseUpErrors.DuplicateSSN + ' Please update the ssn.') {
            setErrorMessage('')
            props.nextStep('4')
          }
          setErrorMessage('')
        }}
        onCancel={() => {
          setErrorMessage('')
        }}
        title={'Error'}
        description={errorMessage}
        type={'error'}
        btn2={'Ok'}
      />
    </div>
  )
}

export default FinancialStanding
